/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { getEntities as getAccountingTransactions } from 'app/entities/accounting-transaction/accounting-transaction.reducer';
import { getEntities as getAccountingBankAccounts } from 'app/entities/accounting-bank-account/accounting-bank-account.reducer';
import { createEntity, getEntity, reset, updateEntity } from './accounting-tax.reducer';

export const AccountingTaxUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const accountingTransactions = useAppSelector(state => state.accountingTransaction.entities);
  const accountingBankAccounts = useAppSelector(state => state.accountingBankAccount.entities);
  const accountingTaxEntity = useAppSelector(state => state.accountingTax.entity);
  const loading = useAppSelector(state => state.accountingTax.loading);
  const updating = useAppSelector(state => state.accountingTax.updating);
  const updateSuccess = useAppSelector(state => state.accountingTax.updateSuccess);

  const handleClose = () => {
    navigate(`/accounting-tax${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getClinicalDictionaries({}));
    dispatch(getAccountingTransactions({}));
    dispatch(getAccountingBankAccounts({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...accountingTaxEntity,
      ...values,
      type: clinicalDictionaries.find(it => it.id.toString() === values.type?.toString()),
      transactions: mapIdList(values.transactions),
      accounts: mapIdList(values.accounts),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...accountingTaxEntity,
          type: accountingTaxEntity?.type?.id,
          transactions: accountingTaxEntity?.transactions?.map(e => e.id.toString()),
          accounts: accountingTaxEntity?.accounts?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.accountingTax.home.createOrEditLabel" data-cy="AccountingTaxCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.accountingTax.home.createOrEditLabel">Create or edit a AccountingTax</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="accounting-tax-id"
                  label={translate('tinyMedicRestApp.accountingTax.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.name')}
                id="accounting-tax-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <UncontrolledTooltip target="nameLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.name" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.rate')}
                id="accounting-tax-rate"
                name="rate"
                data-cy="rate"
                type="text"
              />
              <UncontrolledTooltip target="rateLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.rate" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.country')}
                id="accounting-tax-country"
                name="country"
                data-cy="country"
                type="text"
              />
              <UncontrolledTooltip target="countryLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.country" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.active')}
                id="accounting-tax-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.cursor')}
                id="accounting-tax-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.lastUpdated')}
                id="accounting-tax-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.microSec')}
                id="accounting-tax-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.timestampEntry')}
                id="accounting-tax-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.accountingTax.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="accounting-tax-type"
                name="type"
                data-cy="type"
                label={translate('tinyMedicRestApp.accountingTax.type')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.transaction')}
                id="accounting-tax-transaction"
                data-cy="transaction"
                type="select"
                multiple
                name="transactions"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingTax.account')}
                id="accounting-tax-account"
                data-cy="account"
                type="select"
                multiple
                name="accounts"
              >
                <option value="" key="0" />
                {accountingBankAccounts
                  ? accountingBankAccounts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/accounting-tax" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AccountingTaxUpdate;
